import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Step, StepLabel, Stepper } from '@mui/material'
import type { StepConfig } from '../types'
import { KeyRingSetupStep } from './steps/KeyRingSetupStep'
import { ApiKeySetupStep } from './steps/ApiKeySetupStep'
import { HostSetupStep } from './steps/HostSetupStep'

interface OnboardingWizardProps {
  readonly steps: readonly StepConfig[]
  readonly isReturningUser: boolean
  readonly onClose: () => void
}

export const OnboardingWizard: FC<OnboardingWizardProps> = ({
  steps,
  isReturningUser,
  onClose,
}) => {
  const [snapshotSteps] = useState(() => steps)
  const [activeStepIndex, setActiveStepIndex] = useState(0)

  const handleStepComplete = useCallback(() => {
    if (activeStepIndex >= snapshotSteps.length - 1) {
      onClose()
      return
    }
    setActiveStepIndex((prev) => prev + 1)
  }, [activeStepIndex, snapshotSteps.length, onClose])

  const currentStep = snapshotSteps[activeStepIndex]

  if (!currentStep) return null

  return (
    <Box aria-label="Onboarding setup">
      {snapshotSteps.length > 1 && (
        <Stepper activeStep={activeStepIndex} sx={stepperSx}>
          {snapshotSteps.map((step) => (
            <Step key={step.id}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box sx={stepContentSx}>
        {currentStep.id === 'keyRing' && (
          <KeyRingSetupStep isReturningUser={isReturningUser} onStepComplete={handleStepComplete} />
        )}
        {currentStep.id === 'apiKey' && <ApiKeySetupStep onStepComplete={handleStepComplete} />}
        {currentStep.id === 'host' && <HostSetupStep onStepComplete={handleStepComplete} />}
      </Box>
    </Box>
  )
}

const stepperSx: SxProps<Theme> = {
  mb: 4,
}

const stepContentSx: SxProps<Theme> = {
  minHeight: 200,
}
